import * as ngCdkOverlay from '@angular/cdk/overlay'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as cb from '@attain-apps/fe/web/main/lib/clipboard'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as featureFlags from '@attain-apps/feature-flags'
import * as awsAmplifyAuth from 'aws-amplify/auth'

import * as state from '../../../../state'
import { Store } from '../store'

import * as apiKeysSlideout from './api-keys-slideout'
import * as assistantSlideout from './assistant-slideout'
import * as fileFinder from './file-finder'
import * as item from './item'
import * as processSlideout from './process-slideout'

/**
 * Footer actions for the platform navbar.
 */
@ngCore.Component({
  imports: [
    ngCdkOverlay.CdkOverlayOrigin,
    item.Component,
    apiKeysSlideout.Component,
    assistantSlideout.Component,
    processSlideout.Component,
    fileFinder.Component,
    ds.overlay.confirmation.Component,
  ],
  selector: 'atn-navbar-footer',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * URL of the documentation site
   */
  public readonly docsUrl = `https://docs.${
    window.location.host.startsWith('localhost:')
      ? 'dev.attainapps.com'
      : window.location.host
  }`

  /** @see {@link featureFlags.tryGet} */
  public readonly featureFlags = featureFlags.tryGet()

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.display.toaster.Feature} */
  private readonly _toasterFeature = ngCore.inject(
    state.display.toaster.Feature,
  )

  /** @see {@link cb.Service} */
  public readonly clipboard = ngCore.inject(cb.Service)

  /** @see {@link state.summit.checkIn.Feature} */
  public readonly checkInFeature = ngCore.inject(state.summit.checkIn.Feature)

  /** @see {@link state.fileMgmt.Feature} */
  public readonly fileMgmtFeature = ngCore.inject(state.fileMgmt.Feature)

  /** @see {@link state.assistants.Assistants} */
  public readonly assistants = ngCore.inject(state.assistants.Assistants)

  /** @see {@link state.apiKeys.ApiKeys} */
  public readonly apiKeys = ngCore.inject(state.apiKeys.ApiKeys)

  /** @see {@link state.relay.processes.Feature} */
  public readonly processesFeature = ngCore.inject(
    state.relay.processes.Feature,
  )

  /** @see {@link Store} */
  private readonly _store = ngCore.inject(Store)

  /** Indicates if log out confirmation dialog is open */
  public showLogOutConfirmationSg = ngCore.signal(false)

  /** Indicates if the API access slideout is open */
  public readonly apiKeysSlideoutOpenSg = ngCore.signal(false)

  /** Opens the process slideout */
  public openProcessSlideout(): void {
    this.processesFeature.actions.openSlideout
      .from('Footer.ProcessIcon.Clicked')
      .dispatch()
  }

  /** Copies the current context to the browser clipboard. */
  public copyContext(): void {
    const contextWithAliases =
      this.meFeature.contexts.selectors.currentContextWithAliases.asSignal()
    if (!contextWithAliases) {
      return
    }

    void navigator.clipboard
      .writeText(JSON.stringify(contextWithAliases))
      .then(() => {
        this._toasterFeature.actions.show
          .from('Navbar.CopyContext.Clicked')
          .dispatch({
            toast: {
              description: 'The context was copied into the clipboard.',
              name: 'Context copied',
              style: ents.core.display.Style.Success,
            },
          })
      })
  }

  /**
   * Opens clipboard overlay so user can see what is in their clipboard
   */
  public openClipboard(event: Event): void {
    this.clipboard.openDialog
      .from('ProductsSidebar.Clipboard.Icon.Clicked')
      .dispatch({
        dialogConfig: {
          offset: {
            // TODO width is changed dynamically so we need to find a way to to
            // manage that in clipboard
            x: this._store.widthSg() ?? 0,
            y: 95,
          },
          // TODO update origin position, it shouldn't be from event but from
          // footer element
          originPosition: cb.models.Coordinates.from(event),
        },
      })
  }

  /**
   * Logs the user out
   */
  public async signOut(): Promise<void> {
    this.showLogOutConfirmationSg.set(false)
    await awsAmplifyAuth.signOut()
  }
}
